import { DesignThemes, DesignTokens } from '@workbuddy/theme';

export type DesignSystemStyleValue = string | number | null | undefined | false;

export type DesignSystemRuleSet = Record<string, DesignSystemStyleValue>;

export type DesignSystemStyleSheet = Record<string, DesignSystemRuleSet>;

export class DesignSystem {
    private static readonly mountedStyleIds = new WeakMap<Document, Set<string>>();

    static readonly tokens = DesignTokens;

    static readonly themes = DesignThemes;

    static declarations(rules: DesignSystemRuleSet): string {
        return Object.entries(rules)
            .filter(([, value]) => value !== null && value !== undefined && value !== false)
            .map(([property, value]) => `${this.toKebabCase(property)}: ${value};`)
            .join('\n');
    }

    static rule(selector: string, rules: DesignSystemRuleSet): string {
        const declarations = this.declarations(rules);

        return declarations ? `${selector} {\n${declarations}\n}` : '';
    }

    static stylesheet(sheet: DesignSystemStyleSheet): string {
        return Object.entries(sheet)
            .map(([selector, rules]) => this.rule(selector, rules))
            .filter(Boolean)
            .join('\n\n');
    }

    static Mount(documentRef: Document | null | undefined, id: string, cssOrSheet: string | DesignSystemStyleSheet): HTMLStyleElement | null {
        if (!documentRef?.head) {
            return null;
        }

        const css = typeof cssOrSheet === 'string' ? cssOrSheet : this.stylesheet(cssOrSheet);
        const mountedStyleIds = this.getMountedStyleIds(documentRef);
        const existingStyleElement = documentRef.head.querySelector(`style[data-wb-style-id="${id}"]`) as HTMLStyleElement | null;

        if (existingStyleElement) {
            if (existingStyleElement.textContent !== css) {
                existingStyleElement.textContent = css;
            }

            mountedStyleIds.add(id);
            return existingStyleElement;
        }

        const styleElement = documentRef.createElement('style');
        styleElement.setAttribute('data-wb-style-id', id);
        styleElement.textContent = css;
        documentRef.head.appendChild(styleElement);
        mountedStyleIds.add(id);

        return styleElement;
    }

    static mount(documentRef: Document | null | undefined, id: string, cssOrSheet: string | DesignSystemStyleSheet): HTMLStyleElement | null {
        return this.Mount(documentRef, id, cssOrSheet);
    }

    private static toKebabCase(property: string): string {
        return property.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
    }

    private static getMountedStyleIds(documentRef: Document): Set<string> {
        let mountedStyleIds = this.mountedStyleIds.get(documentRef);

        if (!mountedStyleIds) {
            mountedStyleIds = new Set<string>();
            this.mountedStyleIds.set(documentRef, mountedStyleIds);
        }

        return mountedStyleIds;
    }
}
